import { CompanyData, Sidebar } from "@bonusx-global/bx-design-system";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BonusXLogo } from "../../../components/blocks/BonusXLogo";
import { PRICING_PAGE_PATH } from "../../../constants/env";
import useIsMobile from "../../../hooks/device/useIsMobile";
import { useCurrentUserSubscriptionTier } from "../../hooks/use-current-user-subscription-tier/use-current-user-subscription-tier";
import { useMutateSearch } from "../../hooks/useSearchModule";
import { BONUSX_SUBSCRIPTION_FREQUENCIES } from "../../services/user-subscription/enums/bonusx-subscription-frequencies.enum";
import { BONUSX_SUBSCRIPTION_TIERS } from "../../services/user-subscription/enums/bonusx-subscription-tiers.enum";
import { BonusXThemeProvider } from "../../theme/BonusXThemeProvider";
import { openRoutePath } from "../BottomNavigationBar/functions/open-route-path/open-route-path";
import { EmptyComponent } from "../common/EmptyComponent";
import { citizenRoutes } from "./citizen-routes";
import { enrichCitizenRoutesWithState } from "./utils/enrich-citizen-routes-with-state";

export const sidebarComponentId = "sidebar-component-id";
export const tabbarComponentId = "tabbar-component-id";

export function SidebarComponent(): JSX.Element {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const mutateSearch = useMutateSearch();
  const { currentUserTier, currentUserTierFrequency } = useCurrentUserSubscriptionTier();

  const isCurrentUserPlanTier3AndYearly = useMemo(
    function memoizeIsCurrentUserPlanTier3AndYearly() {
      return (
        currentUserTier === BONUSX_SUBSCRIPTION_TIERS.TIER_3 &&
        currentUserTierFrequency === BONUSX_SUBSCRIPTION_FREQUENCIES.YEARLY
      );
    },
    [currentUserTier, currentUserTierFrequency]
  );

  const locationPathname = location.pathname;

  const onClickRouteCallback = useCallback(
    function onClickRoute(path?: string) {
      openRoutePath({ path, navigate, currentPath: locationPathname });
      mutateSearch({ visible: false, searchText: "" });
    },
    [navigate, locationPathname]
  );

  const citizenRoutesEnriched = useMemo(
    function memoizeCitizenRoutesEnriched() {
      const routes = isCurrentUserPlanTier3AndYearly
        ? citizenRoutes.filter(function filterUpgradeRoute(route) {
            return route.path !== PRICING_PAGE_PATH;
          })
        : citizenRoutes;

      return enrichCitizenRoutesWithState(routes, locationPathname, onClickRouteCallback);
    },
    [locationPathname, onClickRouteCallback, isCurrentUserPlanTier3AndYearly]
  );

  if (isMobile) {
    return <EmptyComponent />;
  }

  return (
    <BonusXThemeProvider>
      <Sidebar id={sidebarComponentId} logo={<BonusXLogo />} items={citizenRoutesEnriched}>
        <CompanyData />
      </Sidebar>
    </BonusXThemeProvider>
  );
}
